import { Box, SxProps, Theme, Typography } from '@mui/material';
import { ComponentProps } from 'react';
import * as React from 'react';

import {
  NOTIFICATION_THEME_BY_SEVERITY,
  NotificationSeverity,
} from '../constants';

export type CalloutProps = {
  severity: NotificationSeverity;
  textVariant?: ComponentProps<typeof Typography>['variant'];
  sx?: SxProps<Theme>;
  skipTextWrapper?: boolean;
} & React.HTMLProps<HTMLDivElement>;

export const Callout = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<CalloutProps>
>(
  (
    {
      severity,
      textVariant,
      children,
      sx = {},
      skipTextWrapper = false,
      ...htmlProps
    },
    ref
  ) => {
    const theme = NOTIFICATION_THEME_BY_SEVERITY[severity];
    return (
      <Box
        ref={ref}
        sx={{
          p: 2,
          py: 1.5, // should appear the same as the others, counteracting the 4px border
          display: 'flex',
          borderLeft: `solid 8px ${theme.border}`,
          backgroundColor: theme.background,
          alignItems: 'center',
          borderRadius: '4px',
          // Drive text color from the severity theme. Setting `color` on the
          // container and forcing descendant Typography to inherit ensures that
          // custom-content callouts (e.g. a Stack with a title + body) pick up
          // the severity color automatically — MUI Typography variants
          // hard-code their own `color`, so an inheritance override is required.
          color: theme.text,
          '& .MuiTypography-root': { color: 'inherit' },
          ...sx,
        }}
        {...htmlProps}
      >
        {skipTextWrapper ? (
          children
        ) : (
          <Typography component="div" variant={textVariant ?? 'body-xs'}>
            {children}
          </Typography>
        )}
      </Box>
    );
  }
);

Callout.displayName = 'Callout';
